import { Component, computed, inject, input, signal } from "@angular/core";
import { Router } from "@angular/router";
import { ExpandButtonComponent } from "../expand-button/expand-button.component";
import { TranslatePipe } from "../../pipes/translate.pipe";
// ⚠️ `import type` et non `import` : un import de valeur fermait, à l'exécution, le cycle
// confirmation-dialog → usage-detail → expand-button → chips → … → confirmation-dialog,
// né du merge du 03/09/2026 (chaque branche en apportait une moitié). Il ne faisait
// tomber que les TestBed (`ng build` résout les dépendances paresseusement).
import type { ConfirmationDetail } from "../confirmation-dialog/confirmation-dialog.component";

/**
 * La **ventilation dépliable des emplois** : « 24 non-conformités » puis, déplié, chacune nommée et
 * cliquable.
 *
 * ## Pourquoi un composant, et pas deux rendus
 *
 * ⚠️ **Il sert DEUX écrans qui posent la même question à deux moments.** Le dialogue de corbeille
 * (« qu'est-ce que j'archive ? ») et la fiche en lecture seule d'une entrée native (« qui l'emploie ? »).
 * Les rendre séparément, c'était deux mises en forme d'une même donnée, qui auraient divergé — et l'une
 * des deux aurait perdu les liens.
 *
 * ⚠️ **Ce second usage comble un angle mort réel** : une entrée native n'a ni « Enregistrer » ni
 * corbeille, donc son nombre d'emplois n'était affiché nulle part. Sur « Virement », employé par
 * 79 fiches tiers et des transactions, l'écran ne montrait rien du tout.
 *
 * ⚠️ **Replié par défaut** : le compte répond à la question courante, l'inventaire à la suivante.
 */
@Component({
  selector: "vl-usage-detail",
  standalone: true,
  imports: [ExpandButtonComponent, TranslatePipe],
  templateUrl: "./usage-detail.component.html",
  styleUrls: ["./usage-detail.component.scss"],
})
export class UsageDetailComponent {
  /** Les groupes d'emplois, déjà traduits et mis en forme par l'appelant. */
  details = input<ConfirmationDetail[]>([]);

  /**
   * Une ligne d'introduction au-dessus des groupes — le total, quand l'appelant veut l'annoncer.
   *
   * ⚠️ Vide dans le dialogue de corbeille : son message le dit déjà, et le répéter juste en dessous
   * ferait lire deux fois la même phrase.
   */
  intro = input<string>("");

  /** Action à jouer avant de naviguer (fermer un dialogue, typiquement). */
  avantNavigation = input<(() => void) | null>(null);

  deplie = signal(false);

  total = computed(() =>
    this.details().reduce((somme, d) => somme + (d.count ?? 0), 0),
  );

  private router = inject(Router);

  /**
   * Ouvre l'élément visé.
   *
   * ⚠️ **`navigateByUrl` et non `routerLink`** : certaines cibles portent un paramètre de requête
   * (`/non-conformites?openNc=…`, qui ouvre le dialogue de la non-conformité), que `routerLink`
   * interpréterait comme un segment de chemin.
   */
  ouvrir(route: string | null | undefined): void {
    if (!route) return;
    this.avantNavigation()?.();
    void this.router.navigateByUrl(route);
  }
}
